import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';
import Container from '../../../components/layout/Container';

// UI components
import { FAQList } from '../../../components/ui/FAQ';
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import PricingCard from '../../../components/ui/PricingCard';
import Card from '../../../components/ui/Card';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { aiTrainingData } from '../../../data/servicePagesData';
import { caseStudies } from '../../../data/mockData';

/**
 * AI Training & Education Service Page - Clean and Professional Layout
 */
const AITrainingPage: React.FC = () => {
  // Filter case studies based on related case study IDs
  const relatedCaseStudies = caseStudies.filter(study => 
    aiTrainingData.relatedCaseStudies.includes(study.id)
  );

  return (
    <ServiceLayout>
      <Hero
        title={aiTrainingData.title}
        subtitle={aiTrainingData.subtitle}
        ctaText="Schedule a Training Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />
      
      {/* Overview */}
      <ServicePageSection title="Overview" padding="md">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-neutral-600 leading-relaxed">
            {aiTrainingData.overview}
          </p>
        </div>
      </ServicePageSection>

      {/* Key Benefits */}
      <ServicePageSection 
        title="Key Benefits" 
        bgColor="gray" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aiTrainingData.keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              delay={index * 0.1}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
              }
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Programs */}
      <ServicePageSection title="Our Training Programs" padding="md">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {aiTrainingData.programs.map((program, index) => (
            <motion.div
              key={program.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-semibold mb-3">{program.title}</h3>
                <p className="text-neutral-600 mb-4">{program.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-neutral-800">Duration:</span>
                    <p className="text-neutral-600">{program.duration}</p>
                  </div>
                  <div>
                    <span className="font-medium text-neutral-800">For:</span>
                    <p className="text-neutral-600">{program.forWhom}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Implementation Process */}
      <ServicePageSection 
        title="Our Implementation Process" 
        bgColor="primary" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {aiTrainingData.implementationProcess.map((process, index) => (
            <motion.div
              key={process.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProcessStep
                number={process.step}
                title={process.title}
                description={process.description}
              />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Pricing */}
      <ServicePageSection 
        title="Pricing Plans" 
        subtitle="Choose the training package that best fits your organization's size and needs."
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {aiTrainingData.pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex"
            >
              <PricingCard 
                name={tier.name}
                description={tier.description}
                price={tier.price === 'Custom' ? undefined :
                  parseInt(tier.price.replace(/[^0-9]/g, ''))}
                billing={tier.perUnit}
                features={tier.features}
                mostPopular={tier.isPopular}
                ctaText={tier.ctaText}
                ctaLink="/contact"
                className="w-full"
              />
            </motion.div>
          ))}
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-neutral-600">
            Need a custom training solution?{' '}
            <Link to="/contact" className="text-primary font-medium hover:text-primary-700 transition-colors">
              Get in touch for a personalized quote.
            </Link>
          </p>
        </div>
      </ServicePageSection>

      {/* Related Case Studies */}
      <ServicePageSection title="Success Stories" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {relatedCaseStudies.slice(0, 2).map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={study.url} className="block h-full">
                <Card hover className="h-full p-6">
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {study.solution}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                  <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                  
                  <div className="space-y-2">
                    {study.results.slice(0, 2).map((result, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-neutral-600">{result}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <span className="inline-flex items-center text-primary font-medium text-sm">
                      Read full case study
                      <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-8 text-center">
          <Link 
            to="/case-studies" 
            className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
          >
            View all case studies
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </ServicePageSection>

      {/* FAQs */}
      <ServicePageSection 
        title="Frequently Asked Questions" 
        bgColor="gray"
        padding="md"
      >
        <div className="max-w-3xl mx-auto">
          <FAQList faqs={aiTrainingData.faqs} />
          
          <div className="mt-8 text-center">
            <p className="text-neutral-600 mb-4">
              Have more questions about our AI training programs?
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              Contact our training specialists
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to transform your team with AI skills?"
        description="Schedule a consultation to discuss your training needs and discover how our AI education programs can help your organization thrive."
        ctaText="Schedule a Training Consultation"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default AITrainingPage;